<!--
  Which jobs are doing best — the owner's "job comparison section so we can
  see which projects are more profitable", answered twice rather than once.

  Two rankings, side by side, both all-time and both ordered by margin. The
  left one ranks jobs on what the Daily Reports costed; the right one ranks
  them on what actually cleared the bank. They are two lists and not one
  blended score on purpose: a blended score would need the two books added
  together, which is the double count the whole feature exists to remove, and
  it would also hide the single most useful thing on this section — that the
  two lists disagree.

  Where they disagree, the job is marked with how many places it moved, from
  `rankDisagreement`. That gap is the interesting signal: a job the costing
  loves and the bank does not is either owed money, or is quietly costing more
  than any Daily Report has recorded. Both are worth the owner's morning.

  A job with no bank lines is NOT ranked last on the banked side. It is listed
  under it as "not yet banked", because nothing tagged is not the same as
  nothing earned — ranking it last would be a claim about the job rather than
  about the absence of a figure. Same reason `compareJob` leaves a gap null
  instead of writing 0.

  All time, and it says so: money reaches the bank weeks after the pour, so a
  month-scoped ranking would mostly rank jobs by how recently they were
  invoiced. Company scope only — with one job picked there is nothing to rank
  it against, so the screen hides this section rather than drawing a list of
  one.
-->
<script lang="ts">
  import { clearedMarginPct, rankBothBooks, rankBy } from '../../../lib/calc/compare';
  import { money, percent } from '../../../lib/money';
  import type { JobComparison } from '../../../lib/types';

  interface Props {
    /** One row per job, all-time. Never the Untagged row: it is not a job. */
    rows: readonly JobComparison[];
    /**
     * The period the rankings cover, read off the RESPONSE's own scope rather
     * than asserted here — see the same prop on CompareSection. This section
     * asks for a whole-life scope; what it prints is what came back.
     */
    period: string;
    /** Asks the screen to fetch both books again. */
    onretry: () => void;
    loading: boolean;
    failed: boolean;
  }

  const { rows, period, onretry, loading, failed }: Props = $props();

  const trackedRanked = $derived(
    rankBy(
      rows.filter((row) => row.tracked !== null),
      (row) => row.tracked?.margin_pct ?? null
    )
  );

  const bankedRanked = $derived(
    rankBy(
      rows.filter((row) => row.banked !== null),
      clearedMarginPct
    )
  );

  /** Listed rather than ranked — see the header. */
  const notYetBanked = $derived(rows.filter((row) => row.banked === null));

  /**
   * The badge's only input. `trackedRanked` and `bankedRanked` above do NOT
   * share membership — a job with no bank lines is in the former and not the
   * latter — so feeding either one whole into a place-moved comparison would
   * compare a job's place in a ten-job list against its place in a two-job
   * list. `rankBothBooks` ranks both sides over the jobs present in BOTH
   * books instead; see its own doc comment in lib/calc/compare.ts.
   */
  const moved = $derived(rankBothBooks(rows).moved);

  const anyMoved = $derived([...moved.values()].some((places) => places > 0));
</script>

<section class="a-panel" aria-labelledby="jobs-title">
  <div class="a-panel__head">
    <h2 class="a-panel__title" id="jobs-title">Which jobs are doing best</h2>
    <p class="a-panel__sub">{period} · Every job · ex-GST</p>
  </div>

  <p class="a-panel__note">
    Best margin first, with the profit in dollars beside it. Two separate lists, never one blended
    score: the same job can sit high on what it was costed at and low on what has actually cleared.
  </p>

  <!--
    The sentence, on screen and not only in the header comment above. Both
    lists deliberately ignore the picker at the top of the screen, and a
    reader who assumes otherwise reads every ranking here as this month's.
  -->
  <p class="a-panel__note">
    Both lists are all time, whatever the month picker above says: money reaches the bank weeks after
    the concrete does, so a month's ranking would mostly rank jobs by how recently they were invoiced.
  </p>

  {#if failed}
    <!-- Its own button, for the same reason TrackedSection has one. -->
    <p class="meta">The rankings did not come back. Nothing is wrong with them — the request failed.</p>
    <p>
      <button type="button" class="a-cta" onclick={onretry} disabled={loading}>
        {loading ? 'Trying…' : 'Try again'}
      </button>
    </p>
  {:else if loading && rows.length === 0}
    <p class="meta"><span class="spinner" aria-hidden="true"></span> Loading both rankings…</p>
  {:else if rows.length === 0}
    <p class="meta">No job has figures in either book yet.</p>
  {:else}
    <div class="lists">
      <div class="list">
        <h3 class="list__title">
          What we costed
          <small>Daily Reports, at your rates</small>
        </h3>
        {#if trackedRanked.length === 0}
          <p class="meta">No Daily Report has been costed against a job yet.</p>
        {:else}
          <ol class="rank">
            {#each trackedRanked as row, i (row.site_id ?? row.name)}
              <li>
                <span class="rank__no" aria-hidden="true">{i + 1}</span>
                <span class="rank__name">
                  {row.name}
                  {#if (moved.get(row.site_id) ?? 0) > 0}
                    <span class="pill pill--warn rank__moved">
                      moved {moved.get(row.site_id)}
                      {moved.get(row.site_id) === 1 ? 'place' : 'places'}
                    </span>
                  {/if}
                </span>
                <span class="rank__pct a-num" class:is-bad={(row.tracked?.margin_pct ?? 0) < 0}>
                  {percent(row.tracked?.margin_pct)}
                </span>
                <span class="rank__fig a-num" class:is-bad={(row.tracked?.margin ?? 0) < 0}>
                  {money(row.tracked?.margin)}
                </span>
              </li>
            {/each}
          </ol>
        {/if}
      </div>

      <div class="list">
        <h3 class="list__title">
          What cleared the bank
          <small>Imported statements</small>
        </h3>
        {#if bankedRanked.length === 0}
          <p class="meta">No imported bank line carries a job yet.</p>
        {:else}
          <ol class="rank">
            {#each bankedRanked as row, i (row.site_id ?? row.name)}
              <li>
                <span class="rank__no" aria-hidden="true">{i + 1}</span>
                <span class="rank__name">{row.name}</span>
                <span class="rank__pct a-num" class:is-bad={(clearedMarginPct(row) ?? 0) < 0}>
                  {percent(clearedMarginPct(row))}
                </span>
                <span class="rank__fig a-num" class:is-bad={(row.banked?.net ?? 0) < 0}>
                  {money(row.banked?.net)}
                </span>
              </li>
            {/each}
          </ol>
        {/if}

        {#if notYetBanked.length > 0}
          <!--
            Listed, not ranked last. A job nothing has been tagged to has not
            performed badly — there is nothing to rank it on.
          -->
          <p class="notyet__title">Not yet banked</p>
          <ul class="notyet">
            {#each notYetBanked as row (row.site_id ?? row.name)}
              <li>{row.name}</li>
            {/each}
          </ul>
          <p class="a-panel__note">
            Nothing on an imported statement is tagged to {notYetBanked.length === 1 ? 'this job' : 'these jobs'}
            yet, so {notYetBanked.length === 1 ? 'it has' : 'they have'} no cleared figure to be ranked on.
          </p>
        {/if}
      </div>
    </div>

    {#if anyMoved}
      <p class="a-panel__note">
        A job marked <b>moved</b> sits in a different place on the two lists. Most often it ranks higher
        on what we costed than on what has cleared — either waiting on money, or costs that never
        reached a Daily Report.
      </p>
    {/if}
  {/if}
</section>

<style>
  .lists {
    display: grid;
    gap: 0.8rem;
  }
  /* Side by side once there is room. Stacked before that, tracked first —
     the two lists are peers, and neither is the answer on its own. */
  @media (min-width: 40rem) {
    .lists { grid-template-columns: 1fr 1fr; }
  }

  .list { display: grid; gap: 0.4rem; align-content: start; }
  .list__title {
    margin: 0;
    font-size: 0.86rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-soft);
  }
  .list__title small {
    display: block;
    font-size: 0.72rem;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    color: var(--ink-faint);
  }

  .rank {
    display: grid;
    gap: 0.3rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .rank li {
    display: grid;
    grid-template-columns: 1.4rem minmax(0, 1fr) auto auto;
    align-items: baseline;
    gap: 0.1rem 0.5rem;
    padding: 0.4rem 0.55rem;
    border-radius: var(--radius);
    background: var(--bg-sunken);
    font-size: 0.9rem;
  }
  .rank__no {
    font-size: 0.78rem;
    font-weight: 800;
    color: var(--ink-faint);
  }
  .rank__name { font-weight: 700; min-width: 0; }
  .rank__moved {
    margin-left: 0.3rem;
    font-size: 0.66rem;
    vertical-align: 0.05rem;
  }
  .rank__pct { font-weight: 700; }
  .rank__fig { color: var(--ink-soft); }

  /* A loss is a figure, not a fault — but it must not read as a win. */
  .is-bad { color: var(--hold); }

  .notyet__title {
    margin: 0.3rem 0 0;
    font-size: 0.78rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-faint);
  }
  .notyet {
    display: grid;
    gap: 0.2rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .notyet li {
    padding: 0.3rem 0.55rem;
    border-radius: var(--radius);
    border: 1px dashed var(--line);
    font-size: 0.88rem;
    color: var(--ink-soft);
  }
</style>
